import type { DesignDocument, RectMm, ViewId, ViewMetric } from '@ox/schema';

const VIEW_LABELS: Record<string, string> = {
  front: 'delante',
  back: 'espalda',
  left: 'izquierda',
  right: 'derecha',
  sleeve: 'manga',
  other: 'otra',
};

type AreaByView = Partial<Record<ViewId, Pick<RectMm, 'width' | 'height'>>>;

/**
 * AABB of objects clipped to the print area (lienzo).
 * Objects are in mm relative to the print-area origin (0,0)–(areaW, areaH).
 * Overflow is capped to the printable region so metrics never exceed the lienzo.
 */
function aabbClippedToArea(
  objects: Array<{ x: number; y: number; width: number; height: number }>,
  areaW: number,
  areaH: number,
): { widthMm: number; heightMm: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let any = false;
  for (const o of objects) {
    if (!(o.width > 0) || !(o.height > 0)) continue;
    any = true;
    minX = Math.min(minX, o.x);
    minY = Math.min(minY, o.y);
    maxX = Math.max(maxX, o.x + o.width);
    maxY = Math.max(maxY, o.y + o.height);
  }
  if (!any) return null;

  const aw = areaW > 0 ? areaW : Infinity;
  const ah = areaH > 0 ? areaH : Infinity;
  const x0 = Math.max(minX, 0);
  const y0 = Math.max(minY, 0);
  const x1 = Math.min(maxX, aw);
  const y1 = Math.min(maxY, ah);
  const w = x1 - x0;
  const h = y1 - y0;
  if (!(w > 0) || !(h > 0)) {
    // Fully outside — fall back to raw size capped to area (billing still needs a footprint).
    return {
      widthMm: Math.max(0.1, Math.round(Math.min(maxX - minX, aw) * 100) / 100),
      heightMm: Math.max(0.1, Math.round(Math.min(maxY - minY, ah) * 100) / 100),
    };
  }
  return {
    widthMm: Math.max(0.1, Math.round(w * 100) / 100),
    heightMm: Math.max(0.1, Math.round(h * 100) / 100),
  };
}

/** Bounding box metrics for every view that has design objects (clipped to print area). */
export function getUsedViewMetrics(
  document: DesignDocument,
  printAreas?: AreaByView | null,
): ViewMetric[] {
  const out: ViewMetric[] = [];
  for (const view of document.views ?? []) {
    const objects = view.objects ?? [];
    if (!objects.length) continue;
    const area = printAreas?.[view.viewId];
    const box = aabbClippedToArea(objects, area?.width ?? 0, area?.height ?? 0);
    if (!box) continue;
    out.push({
      viewId: view.viewId,
      label: VIEW_LABELS[view.viewId] ?? view.viewId,
      widthMm: box.widthMm,
      heightMm: box.heightMm,
      objectCount: objects.length,
    });
  }
  return out;
}
